import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AppProvider, useApp } from './context/AppContext';
import { Toaster } from '@/components/ui/sonner';
import { PwaUpdateNotice } from '@/components/shared/InstallCTH';
import Landing from './pages/Landing';
import { InviteIntake, PublicVacancy, TalentPoolIntake } from './pages/PublicIntake';
import Login from './pages/Login';
import AdminRoutes from './routes/AdminRoutes';
import ClientRoutes from './routes/ClientRoutes';
import EndUserRoutes from './routes/EndUserRoutes';
import EndUserAssessment from './pages/enduser/EndUserAssessment';
import ChangePassword from './pages/ChangePassword';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import GlobalEthicsPublic from './pages/ethics/GlobalEthicsPublic';
import './i18n';
import './App.css';

const HOME_FOR_ROLE = {
  hm_admin: '/admin',
  hm_advisor: '/admin',
  hm_reviewer: '/admin',
  client_admin: '/client',
  client_hr: '/client',
  client_viewer: '/client',
  evaluated: '/me',
};

const Protected = ({ allow, children }) => {
  const { user, authReady } = useApp();
  const location = useLocation();
  if (!authReady) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-sm text-muted-foreground">Loading…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  // Iter 41.39 — must_change_password hard gate. Any authenticated user
  // with the flag set is force-redirected to /change-password and cannot
  // bypass via direct URL navigation. The change-password screen itself
  // is rendered through a separate route (no Protected wrap) so users
  // can reach it; once cleared, normal routing resumes.
  if (user.must_change_password && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }
  if (allow && !allow.includes(user.role)) {
    const home = HOME_FOR_ROLE[user.role] || '/login';
    return <Navigate to={home} replace />;
  }
  return children;
};

// Iter 41.39 — change-password route is reachable only when authenticated.
// Once `must_change_password=false`, redirect away so the user lands back
// on the role homepage.
const ChangePasswordRoute = () => {
  const { user, authReady } = useApp();
  if (!authReady) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-sm text-muted-foreground">Loading…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  // If the flag is already false and the user reached this URL manually,
  // bounce them home so the screen doesn't become a hidden access vector.
  // (The page itself still allows ?force=1 for voluntary rotation.)
  return <ChangePassword />;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/apply/i/:token" element={<InviteIntake />} />
      <Route path="/jobs/:slug" element={<PublicVacancy />} />
      <Route path="/talent-pool" element={<TalentPoolIntake />} />
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/ethics" element={<GlobalEthicsPublic />} />
      <Route path="/change-password" element={<ChangePasswordRoute />} />
      <Route path="/admin/*" element={<Protected allow={["hm_admin","hm_advisor","hm_reviewer"]}><AdminRoutes /></Protected>} />
      <Route path="/client/*" element={<Protected allow={["client_admin","client_hr","client_viewer"]}><ClientRoutes /></Protected>} />
      <Route path="/me/*" element={<Protected allow={["evaluated", "client_admin", "client_hr", "client_viewer"]}><EndUserRoutes /></Protected>} />
      <Route path="/assessment/:id" element={<Protected allow={["evaluated"]}><EndUserAssessment /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AppProvider>
      <BrowserRouter>
        <AppRoutes />
        <PwaUpdateNotice />
        <Toaster position="top-right" richColors closeButton />
      </BrowserRouter>
    </AppProvider>
  );
}
